import { createReducer, on } from '@ngrx/store';
import { CartState, CartItem, StoreCart } from './cart.models';
import * as CartActions from './cart.actions';

/**
 * Load cart from localStorage
 */
export function loadCartFromStorage(): CartState {
  try {
    const stored = localStorage.getItem('jujuja_cart');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects
      if (parsed.stores) {
        Object.keys(parsed.stores).forEach(storeId => {
          const storeCart = parsed.stores[storeId];
          if (storeCart.items) {
            storeCart.items = storeCart.items.map((item: any) => ({
              ...item,
              date: item.date ? new Date(item.date) : new Date(),
              startTime: item.startTime ? new Date(item.startTime) : new Date(),
            }));
          }
        });
      }
      return parsed;
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return initialState;
}

/**
 * Save cart to localStorage
 */
export function saveCartToStorage(state: CartState): void {
  try {
    localStorage.setItem('jujuja_cart', JSON.stringify(state));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
}

/**
 * Helper function to calculate store cart totals
 */
function calculateStoreCartTotals(items: CartItem[]): { totalPrice: number; totalDuration: number } {
  return items.reduce(
    (totals, item) => ({
      totalPrice: totals.totalPrice + item.totalPrice,
      totalDuration: totals.totalDuration + item.duration,
    }),
    { totalPrice: 0, totalDuration: 0 }
  );
}

/**
 * Helper function to calculate overall cart totals
 */
function calculateCartTotals(stores: Record<string, StoreCart>): { itemCount: number; totalPrice: number } {
  let itemCount = 0;
  let totalPrice = 0;

  Object.values(stores).forEach((storeCart) => {
    itemCount += storeCart.items.length;
    totalPrice += storeCart.totalPrice;
  });

  return { itemCount, totalPrice };
}

/**
 * Helper function to create or update a store cart
 */
function createOrUpdateStoreCart(
  stores: Record<string, StoreCart>,
  item: CartItem
): Record<string, StoreCart> {
  const existingStoreCart = stores[item.storeId];

  if (existingStoreCart) {
    // Store exists, add item to it
    const updatedItems = [...existingStoreCart.items, item];
    const { totalPrice, totalDuration } = calculateStoreCartTotals(updatedItems);

    return {
      ...stores,
      [item.storeId]: {
        ...existingStoreCart,
        items: updatedItems,
        totalPrice,
        totalDuration,
        storeName: item.storeName || existingStoreCart.storeName,
        storeAddress: item.storeAddress || existingStoreCart.storeAddress,
      },
    };
  } else {
    // New store, create new cart
    const { totalPrice, totalDuration } = calculateStoreCartTotals([item]);

    return {
      ...stores,
      [item.storeId]: {
        storeId: item.storeId,
        storeName: item.storeName,
        storeAddress: item.storeAddress,
        items: [item],
        totalPrice,
        totalDuration,
      },
    };
  }
}

const defaultInitialState: CartState = {
  stores: {},
  itemCount: 0,
  totalPrice: 0,
  isLoading: false,
  error: null,
};

export const initialState: CartState = (() => {
  try {
    const loaded = loadCartFromStorage();
    // Validate loaded state
    if (loaded && loaded.stores && typeof loaded.stores === 'object') {
      return loaded;
    }
  } catch (error) {
    console.error('Error initializing cart from localStorage:', error);
  }
  return defaultInitialState;
})();

export const cartReducer = createReducer(
  initialState,
  on(CartActions.addCartItem, (state, { item }) => {
    const updatedStores = createOrUpdateStoreCart(state.stores, item);
    const { itemCount, totalPrice } = calculateCartTotals(updatedStores);

    const newState = {
      ...state,
      stores: updatedStores,
      itemCount,
      totalPrice,
      error: null,
    };
    saveCartToStorage(newState);
    return newState;
  }),
  on(CartActions.removeCartItem, (state, { storeId, itemId }) => {
    const storeCart = state.stores[storeId];
    if (!storeCart) {
      return state;
    }

    const updatedItems = storeCart.items.filter((item) => item.id !== itemId);

    if (updatedItems.length === 0) {
      // Remove store from cart if no items left
      const { [storeId]: removed, ...remainingStores } = state.stores;
      const { itemCount, totalPrice } = calculateCartTotals(remainingStores);

      const newState = {
        ...state,
        stores: remainingStores,
        itemCount,
        totalPrice,
        error: null,
      };
      saveCartToStorage(newState);
      return newState;
    }

    // Update store cart with remaining items
    const { totalPrice, totalDuration } = calculateStoreCartTotals(updatedItems);
    const updatedStores = {
      ...state.stores,
      [storeId]: {
        ...storeCart,
        items: updatedItems,
        totalPrice,
        totalDuration,
      },
    };

    const { itemCount, totalPrice: overallTotalPrice } = calculateCartTotals(updatedStores);

    const newState = {
      ...state,
      stores: updatedStores,
      itemCount,
      totalPrice: overallTotalPrice,
      error: null,
    };
    saveCartToStorage(newState);
    return newState;
  }),
  on(CartActions.updateCartItem, (state, { storeId, itemId, updates }) => {
    const storeCart = state.stores[storeId];
    if (!storeCart) {
      return state;
    }

    const updatedItems = storeCart.items.map((item) =>
      item.id === itemId ? { ...item, ...updates } : item
    );

    const { totalPrice, totalDuration } = calculateStoreCartTotals(updatedItems);
    const updatedStores = {
      ...state.stores,
      [storeId]: {
        ...storeCart,
        items: updatedItems,
        totalPrice,
        totalDuration,
      },
    };

    const { itemCount, totalPrice: overallTotalPrice } = calculateCartTotals(updatedStores);

    const newState = {
      ...state,
      stores: updatedStores,
      itemCount,
      totalPrice: overallTotalPrice,
      error: null,
    };
    saveCartToStorage(newState);
    return newState;
  }),
  on(CartActions.clearStoreCart, (state, { storeId }) => {
    const { [storeId]: removed, ...remainingStores } = state.stores;
    const { itemCount, totalPrice } = calculateCartTotals(remainingStores);

    const newState = {
      ...state,
      stores: remainingStores,
      itemCount,
      totalPrice,
      error: null,
    };
    saveCartToStorage(newState);
    return newState;
  }),
  on(CartActions.clearCart, () => {
    const newState = defaultInitialState;
    saveCartToStorage(newState);
    return newState;
  }),
  on(CartActions.loadCart, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(CartActions.loadCartSuccess, (state, { stores }) => {
    const { itemCount, totalPrice } = calculateCartTotals(stores);

    const newState = {
      ...state,
      stores,
      itemCount,
      totalPrice,
      isLoading: false,
      error: null,
    };
    saveCartToStorage(newState);
    return newState;
  }),
  on(CartActions.loadCartFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  }))
);

